import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';



const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);

  useEffect(() => {
    // Get initial session from Supabase
    const getInitialSession = async () => {
      setLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          await fetchUserProfile(session.user.id);
        } else {
          setUser(null);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setLoading(true);
        if (session?.user) {
          setUser(session.user);
          fetchUserProfile(session.user.id);
        } else {
          setUser(null);
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserProfile = async (userId) => {
    try {
      const query = supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Profile fetch timeout')), 10000)
      );
      
      const { data, error } = await Promise.race([query, timeoutPromise]);

      if (error) {
        // Try alternative query without .single()
        try {
          const { data: altData } = await supabase
            .from('users')
            .select('*')
            .eq('id', userId);

          if (altData && altData.length > 0) {
            setUser(altData[0]);
            return;
          }
        } catch (altError) {
        }
        
        // If user profile doesn't exist, create a basic one
        if (error.code === 'PGRST116') {
          const { data: newUser, error: createError } = await supabase
            .from('users')
            .insert([{
              id: userId,
              email: 'user@example.com', // This will be updated
              name: 'User',
              role: 'attendee',
              registered: true,
              paid: false,
              public_profile: false
            }])
            .select()
            .single();
            
          if (createError) {
            return;
          }
          
          setUser(newUser);
          return;
        }
        
        return;
      }

      setUser(data);
      return data;
    } catch (error) {
      if (error.message === 'Profile fetch timeout') {
      }
      return null;
    }
  };

  const login = async (email, password) => {
    setAuthLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (data.user) {
        // Fetch the actual user profile from database to get correct role
        await fetchUserProfile(data.user.id);
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setAuthLoading(false);
    }
  };

  const registerWithTicket = async (userData, ticketData) => {
    setAuthLoading(true);
    try {
      // Create Supabase auth user with password (auto-confirm)
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          emailRedirectTo: undefined // Disable email confirmation
        }
      });

      if (authError) {
        return { success: false, error: authError.message };
      }

      if (!authData.user) {
        return { success: false, error: 'Failed to create account' };
      }

      // If user needs email confirmation, sign them in anyway for development
      if (!authData.user.email_confirmed_at) {
        // For development, we'll proceed anyway
      }

      // Create user profile in database
      const { error: profileError } = await supabase
        .from('users')
        .insert([{
          id: authData.user.id,
          email: userData.email,
          name: userData.name,
          phone: userData.phone,
          company: userData.company,
          job_title: userData.jobTitle,
          role: ticketData.role,
          registered: true,
          paid: true,
          // Jia: Include information about bio
          bio: userData.bio,
          links: userData.links,
          clubs: userData.clubs,
          tech_interests: userData.tech_interests,
          dietary_preference: userData.dietary_preference || 'none',
          public_profile: false

          // ticket_number: userData.ticketNumber // Temporarily disabled until column is added
        }])
        .select()
        .single();

      if (profileError) {
        // Try to clean up the auth user if profile creation fails
        await supabase.auth.admin.deleteUser(authData.user.id);
        return { success: false, error: 'Failed to create profile: ' + profileError.message };
      }

      // Set user in context
      const user = {
        id: authData.user.id,
        email: userData.email,
        name: userData.name,
        phone: userData.phone,
        company: userData.company,
        jobTitle: userData.jobTitle,
        job_title: userData.jobTitle,
        role: ticketData.role,
        registered: true,
        paid: true,
        // Jia added columns
          bio: userData.bio,
          links: userData.links,
          clubs: userData.clubs,
          ticket_number: userData.ticketNumber,
          tech_interests: userData.tech_interests,
          dietary_preference: userData.dietary_preference || 'none'

      };

      setUser(user);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setAuthLoading(false);
    }
  };

  const register = async (userData) => {
    setAuthLoading(true);
    try {
      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password
      });

      if (authError) {
        return { success: false, error: authError.message };
      }

      if (authData.user) {
        // Create user profile
        const { error: profileError } = await supabase
          .from('users')
          .insert([
            {
              id: authData.user.id,
              email: userData.email,
              name: userData.name,
              phone: userData.phone,
              company: userData.company,
              job_title: userData.jobTitle,
              role: 'attendee',
              registered: true,
              paid: false,
              // Jia added columns
              bio: userData.bio,
              links: userData.links,
              clubs: userData.clubs,
              tech_interests: userData.tech_interests,
              dietary_preference: userData.dietary_preference || 'none',
              public_profile: false
            }
          ]);

        if (profileError) {
          return { success: false, error: 'Failed to create user profile' };
        }

        await fetchUserProfile(authData.user.id);
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = async () => {
    setAuthLoading(true);
    try {
      await supabase.auth.signOut();
      setUser(null);
    } catch (error) {
    } finally {
      setAuthLoading(false);
    }
  };

  const updateUser = async (updates) => {
    if (!user) return;
    
    // Map form fields to database column names
    const dbUpdates = {
      name: updates.name,
      email: updates.email,
      phone: updates.phone,
      company: updates.company,
      job_title: updates.jobTitle,
      bio: updates.bio ?? '',
      links: updates.links ?? '',
      clubs: updates.clubs ?? [],
      tech_interests: updates.tech_interests ?? [],
      dietary_preference: updates.dietary_preference ?? 'none',
      avatar_url: updates.avatar_url,
      public_profile: typeof updates.public_profile === 'boolean' ? updates.public_profile : undefined

    };
    
    // Remove undefined values
    Object.keys(dbUpdates).forEach(key => {
      if (dbUpdates[key] === undefined) {
        delete dbUpdates[key];
      }
    });
    
    try {
      // Use ONLY Supabase client - no direct HTTP
      const { error } = await supabase
        .from('users')
        .update(dbUpdates)
        .eq('id', user.id)
        .select()
        .single();
        
      if (error) {
        return { success: false, message: error.message };
      }
      
      // Update local state
      const updatedUser = { 
        ...user, 
        name: updates.name,
        email: updates.email,
        phone: updates.phone,
        company: updates.company,
        jobTitle: updates.jobTitle,
        job_title: updates.jobTitle,
        bio: updates.bio ?? '',
        links: updates.links ?? '',
        clubs: updates.clubs ?? [],
        tech_interests: updates.tech_interests ?? [],
        dietary_preference: updates.dietary_preference ?? 'none',
        public_profile: typeof updates.public_profile === 'boolean' ? updates.public_profile : user.public_profile
      };
      setUser(updatedUser);
      
      return { success: true, message: 'Profile updated successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  // Helper function to check if current user is admin
  const isAdmin = user?.role === 'admin';

  const value = {
    user,
    isAdmin,
    login,
    register,
    registerWithTicket,
    logout,
    updateUser,
    fetchUserProfile,
    loading,
    authLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 